import React from "react";
import { useTranslation } from "utilities/i18n";
import { getMTRAppDeviceId } from "utilities/roomUtils";
import { handleNullFields } from "utilities/stringUtils";
import { Link } from "polyrouter";
import { RoomQuery, useMtrRoomDevicesQuery } from "generated/graphql";
import { Layout } from "views/Main/Layouts";
import {
  CategoryTitle,
  ConnectionStatusIcon,
  LoadingIndicator,
  ProductImageInContainer,
  PolyIcon,
  ToolTip
} from "elements";
import { generatePath } from "react-router";
import { FAMILY_IDS, NBSP } from "Constants";
import { Card } from "collections/Cards";

export const MTRRoomDetails: React.FC<{
  room: NonNullable<RoomQuery["room"]>;
}> = ({ room }) => {
  const { Content } = Layout;
  const { t } = useTranslation();

  return (
    <>
      <Content.Title>
        <CategoryTitle title={t("OVERVIEW")} subtitle={room.name} />
      </Content.Title>
      <Content.ScrollingList>
        <MTRRoomDevices room={room} />
      </Content.ScrollingList>
    </>
  );
};

export const MTRRoomDevices: React.FC<{
  room: NonNullable<RoomQuery["room"]>;
}> = ({ room }) => {
  const { t } = useTranslation();
  const { data, fetching } = useMtrRoomDevicesQuery({
    variables: { id: room.id }
  });

  if (fetching) {
    return <LoadingIndicator />;
  }

  const mtrAppDeviceId = getMTRAppDeviceId(room);
  const devices =
    data?.room?.devices?.filter(device => device.id !== mtrAppDeviceId) || [];

  const primaryDevice = devices.find(
    device => FAMILY_IDS.RoomPC === device?.model?.hardwareFamily?.id
  );

  const connectedDevices = devices.filter(
    device => FAMILY_IDS.RoomPC !== device?.model?.hardwareFamily?.id
  );

  return (
    <>
      <h3 className="mb-1 font-md">{t("PRIMARY_DEVICE")}</h3>
      {!!primaryDevice && (
        <Card className="table-card grid">
          <div className="col-3 text-center model-image">
            <ProductImageInContainer
              hardwareModel={primaryDevice.hardwareModel}
              maxHeight={80}
            />
            <div className="pb-5">
              <ConnectionStatusIcon connected={!!primaryDevice.connected} />
              <Link
                className="font-sm link"
                to={generatePath("/manage/inventory/details/:id", {
                  id: primaryDevice.id
                })}
              >
                {handleNullFields(primaryDevice.name)}
              </Link>
            </div>
          </div>
          <div className="col-3 v-center">
            <label>{t("SERIAL_NUMBER")}</label>
            <div>{primaryDevice.serialNumber || NBSP}</div>
          </div>
          <div className="col-3 v-center">
            <label>{t("MAC_ADDRESS")}</label>
            <div>{primaryDevice.macAddress || NBSP}</div>
          </div>
          <div className="col-3 v-center">
            <label>{t("VERSION_ALT")}</label>
            <div>{primaryDevice.softwareVersion || NBSP}</div>
          </div>
        </Card>
      )}
      <h3 className="mb-1 font-md">{t("CONNECTED_DEVICES")}</h3>
      {!!connectedDevices.length &&
        connectedDevices.map(device => (
          <Card key={device.id} className="table-card grid">
            <div className="col-1 text-center v-center pb-2 relative">
              <ToolTip
                iconTitle={t("STATUS_INDICATORS")}
                className="secondary-device-link align-tooltip-icon"
                position="bottom"
                size="sm"
                iconReplacement={
                  <PolyIcon
                    title=""
                    icon={device.connected ? "linked_yes" : "linked_no"}
                    className={`linked-icon ${
                      device.connected ? "" : "disconnected-link"
                    }`}
                    data-testid={`connected-icon-${
                      device.serialNumber
                        ? device.serialNumber
                        : device.macAddress
                    }`}
                  />
                }
              >
                <p>
                  {device.connected
                    ? t("DEVICE_CONNECTED")
                    : t("DEVICE_DISCONNECTED")}
                </p>
              </ToolTip>
            </div>
            <div className="col-2 pv-5 text-center model-image">
              <ProductImageInContainer
                hardwareModel={device.hardwareModel}
                maxHeight={80}
              />
              <div>
                <ConnectionStatusIcon
                  connected={!!device.connected}
                  className="ml-0"
                />
                <Link
                  className="font-sm link"
                  to={generatePath("/manage/inventory/details/:id", {
                    id: device.id
                  })}
                >
                  {handleNullFields(device.name)}
                </Link>
              </div>
            </div>
            <div className="col-3 v-center">
              <label>{t("SERIAL_NUMBER")}</label>
              <div>{device.serialNumber || NBSP}</div>
            </div>
            <div className="col-3 v-center">
              <label>{t("MAC_ADDRESS")}</label>
              <div>{device.macAddress || NBSP}</div>
            </div>
            <div className="col-3 v-center">
              <label>{t("VERSION_ALT")}</label>
              <div>{device.softwareVersion || NBSP}</div>
            </div>
          </Card>
        ))}
    </>
  );
};
